import { getBackendApiUrl } from "./appConfig.js";

const buildApiUrl = (path) => {
  const baseUrl = getBackendApiUrl();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

export const apiUrl = {
  // Database routes
  dbHealth: () => buildApiUrl("/api/db/health"),
  checkDBConnection: () => buildApiUrl("/api/db/check-connection"),

  // User routes (/api/users prefix)
  users: () => buildApiUrl("/aipd/getUserList"),
  verifyUser: () => buildApiUrl("/api/users/verify"),

  // AIPD user routes (/aipd prefix)
  aipdGetUserList: () => buildApiUrl("/aipd/getUserList"),
  aipdGetUserDetails: () => buildApiUrl("/aipd/getUserDetails"),
  aipdUserListByEmail: () => buildApiUrl("/aipd/userListByEmail"),
  aipdUpdateUser: () => buildApiUrl("/aipd/updateUser"),
  aipdRegisterUser: () => buildApiUrl("/aipd/registerUser"),
  aipdDeleteUser: () => buildApiUrl("/aipd/deleteUser"),
  aipdGetUserGroupListForUser: () => buildApiUrl("/aipd/getUserGroupListForUser"),
  aipdUpdateDefaultUserGroup: () => buildApiUrl("/aipd/updateDefaultUserGroup"),
  aipdUpdateDefaultRlType: () => buildApiUrl("/aipd/updateDefaultRlType"),
  aipdNotifications: () => buildApiUrl("/aipd/message/notifications"),
  aipdNotificationStatus: () => buildApiUrl("/aipd/notification/status"),
  aipdReadAllNotifications: () => buildApiUrl("/aipd/notification/readAllNotifications"),
  aipdCreateCommentV2: () => buildApiUrl("/aipd/createComment/v2"),

  // AIPD overview & menu
  aipdGetOverview: () => buildApiUrl("/aipd/getOverview"),
  aipdGetReportByGroup: (groupId) => buildApiUrl(`/aipd/getreport?groupId=${groupId}`),
  aipdGetMenu: () => buildApiUrl("/aipd/getMenu"),
  aipdCompleteTracking: () => buildApiUrl("/aipd/completeTracking"),
  aipdReportsByGroup: (userGroupId) => buildApiUrl(`/aipd/userGroupId/reports?userGroupId=${userGroupId}`),
  aipdAnalytics: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.set("startDate", params.startDate);
    if (params.endDate)   q.set("endDate",   params.endDate);
    if (params.reportId)  q.set("reportId",  params.reportId);
    if (params.personaId) q.set("personaId", params.personaId);
    if (params.userEmail) q.set("userEmail", params.userEmail);
    const qs = q.toString();
    return buildApiUrl(`/aipd/analytics${qs ? `?${qs}` : ""}`);
  },
  aipdUserReportListByGroup: (userGroupId) => buildApiUrl(`/aipd/userReportListByUserGroupId?userGroupId=${userGroupId}`),
  aipdTrackingByUser: (email) => buildApiUrl(`/aipd/trackingByUser?email=${email}`),
  aipdTrackingTimedByUser: (start, end, email) => buildApiUrl(`/aipd/trackingTimedByUser?start=${start}&end=${end}&email=${email}`),
  aipdViewsPerReportByPersonaWithTimeForEmail: (startDate, endDate, email) => buildApiUrl(`/aipd/viewsPerReportByPersonaWithTimeForEmail?startDate=${startDate}&endDate=${endDate}&email=${email}`),

  // AIPD reports & content
  aipdReportData: (reportId) => buildApiUrl(`/aipd/reports/${reportId}`),
  aipdGetFaq: (reportId, groupId) => buildApiUrl(`/aipd/getFaq/reportId/${reportId}${groupId ? `?groupId=${groupId}` : ""}`),
  aipdGetAllFaqs: () => buildApiUrl("/aipd/getFaq"),
  aipdPostFaq: () => buildApiUrl("/aipd/postFaq"),
  aipdUpdateFaq: () => buildApiUrl("/aipd/updateFaq"),
  aipdDeleteFaq: () => buildApiUrl("/aipd/deleteFaq"),
  aipdPosts: (topicIdentifier) => buildApiUrl(`/aipd/posts/v2/${topicIdentifier}`),
  aipdUserEmailsByUserGroupId: () => buildApiUrl("/aipd/userEmailsByUserGroupId"),
  aipdCreateTopicV2: () => buildApiUrl("/aipd/createTopic/v2"),
  aipdUpdateCommentV2: () => buildApiUrl("/aipd/updateComment/v2"),
  aipdDeleteCommentV2: () => buildApiUrl("/aipd/deleteComment/v2"),
  aipdGetBookmark: () => buildApiUrl("/aipd/getBookmark"),
  aipdGetBookmarks: () => buildApiUrl("/aipd/getBookmarks"),
  aipdSaveBookmarks: () => buildApiUrl("/aipd/saveBookmarks"),
  aipdDeleteBookmarks: () => buildApiUrl("/aipd/deleteBookmarks"),
  aipdUpdateBookmarkTitles: () => buildApiUrl("/aipd/updateBookmarkTitles"),
  aipdTracking: () => buildApiUrl("/aipd/tracking"),

  // AIPD custom visuals
  aipdCustomVisuals: (groupId) => buildApiUrl(`/aipd/customVisuals/getVisualsV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdCustomVisualsV2: (groupId) => buildApiUrl(`/aipd/customVisuals/getCustomVisualsV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdPersonaVisuals: (userGroupId) => buildApiUrl(userGroupId ? `/aipd/customVisuals/personaVisuals?userGroupId=${userGroupId}` : "/aipd/customVisuals/personaVisuals"),
  aipdSelectedVisuals: () => buildApiUrl("/aipd/customVisuals/SelectedVisuals"),
  aipdSelectedVisualsV2: () => buildApiUrl("/aipd/customVisuals/SelectedVisualsV2"),
  aipdSaveCustomVisuals: (groupId) => buildApiUrl(`/aipd/customVisuals/saveCustomVisualForUserV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdAddVisualsToCart: () => buildApiUrl("/aipd/customVisuals/addVisualsToCart"),
  aipdSaveKpiStats: (groupId) => buildApiUrl(`/aipd/saveKpiStats${groupId ? `?groupId=${groupId}` : ""}`),

  // Persona routes
  personas: () => buildApiUrl("/api/personas/list"),
  personaDisplayList: () => buildApiUrl("/api/personas/display-list"),
  personasWithUsers: () => buildApiUrl("/api/personas/with-users"),
  personaByName: (name) => buildApiUrl(`/api/personas/by-name/${name}`),
  personaUserGroups: () => buildApiUrl("/api/personas/user-groups"),
  createPersona: () => buildApiUrl("/api/personas/register"),
  updatePersona: (id) => buildApiUrl(`/api/personas/update/${id}`),
  deletePersona: (id) => buildApiUrl(`/api/personas/delete/${id}`),
  personaMapReports: () => buildApiUrl("/api/personas/map-reports"),
  personaMappedReports: (name) => buildApiUrl(`/api/personas/${name}/mapped-reports`),
  personaMappedReportsById: (id) => buildApiUrl(`/api/personas/${id}/mapped-reports`),

  // Report routes
  reports: () => buildApiUrl("/api/reports/list"),
  reportById: (id) => buildApiUrl(`/api/reports/${id}`),
  createReport: () => buildApiUrl("/api/reports/register"),
  updateReport: (id) => buildApiUrl(`/api/reports/${id}`),
  deleteReport: (id) => buildApiUrl(`/api/reports/${id}`),

  // Role routes
  roles: () => buildApiUrl("/api/roles/list"),
  createRole: () => buildApiUrl("/api/roles/register"),
  updateRole: (id) => buildApiUrl(`/api/roles/${id}`),
  deleteRole: (id) => buildApiUrl(`/api/roles/${id}`),
};

export { buildApiUrl };
